"use client"

import type React from "react"
import { useState, useRef } from "react"
import { View, StyleSheet, PanResponder, Animated } from "react-native"
import { IconButton, useTheme } from "react-native-paper"
import { Play, Pause, Maximize, Tag } from "lucide-react"

interface MapControlsProps {
  onAerialViewToggle: () => void
  isAerialViewActive: boolean
  onShowAllTowers: () => void
  onToggleLabels?: () => void
  showLabels?: boolean
  position?: "left" | "right" | "bottom-left" | "bottom-right"
}

export const MapControls: React.FC<MapControlsProps> = ({
  onAerialViewToggle,
  isAerialViewActive,
  onShowAllTowers,
  onToggleLabels,
  showLabels = true,
  position = "left", // Default position is left
}) => {
  const theme = useTheme()
  const [isDragging, setIsDragging] = useState(false)
  const pan = useRef(new Animated.ValueXY()).current

  // Set up PanResponder for drag functionality
  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onPanResponderGrant: () => {
        setIsDragging(true)
        pan.setOffset({
          x: pan.x._value,
          y: pan.y._value,
        })
      },
      onPanResponderMove: Animated.event([null, { dx: pan.x, dy: pan.y }], { useNativeDriver: false }),
      onPanResponderRelease: () => {
        setIsDragging(false)
        pan.flattenOffset()
      },
    }),
  ).current

  // Determine initial position based on the position prop
  let initialPosition = {}
  switch (position) {
    case "right":
      initialPosition = { top: 10, right: 10 }
      break
    case "bottom-left":
      initialPosition = { bottom: 10, left: 10 }
      break
    case "bottom-right":
      initialPosition = { bottom: 10, right: 10 }
      break
    case "left":
    default:
      initialPosition = { top: 10, left: 10 }
  }

  return (
    <Animated.View
      {...panResponder.panHandlers}
      style={[
        styles.controlsContainer,
        {
          backgroundColor: theme.colors.elevation.level2,
          borderColor: theme.colors.outline,
          transform: [{ translateX: pan.x }, { translateY: pan.y }],
          ...initialPosition,
          opacity: isDragging ? 0.8 : 1,
        },
      ]}
    >
      <View style={styles.dragHandle}>
        <View style={[styles.dragIndicator, { backgroundColor: theme.colors.onSurfaceVariant }]} />
      </View>
      <IconButton
        icon={() => (isAerialViewActive ? <Pause size={20} /> : <Play size={20} />)}
        onPress={onAerialViewToggle}
        size={20}
        iconColor={isAerialViewActive ? theme.colors.error : theme.colors.primary}
        style={styles.controlButton}
        tooltip={isAerialViewActive ? "Stop Aerial Tour" : "Start Aerial Tour"}
      />
      <IconButton
        icon={() => <Maximize size={20} />}
        onPress={onShowAllTowers}
        size={20}
        iconColor={theme.colors.primary}
        style={styles.controlButton}
        tooltip="Show All Towers"
      />
      {onToggleLabels && (
        <IconButton
          icon={() => <Tag size={20} />}
          onPress={onToggleLabels}
          size={20}
          iconColor={showLabels ? theme.colors.primary : theme.colors.outline}
          style={styles.controlButton}
          tooltip={showLabels ? "Hide Labels" : "Show Labels"}
        />
      )}
    </Animated.View>
  )
}

const styles = StyleSheet.create({
  controlsContainer: {
    position: "absolute",
    borderRadius: 4,
    borderWidth: 1,
    padding: 4,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  controlButton: {
    margin: 0,
  },
  dragHandle: {
    width: "100%",
    height: 12,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 4,
  },
  dragIndicator: {
    width: 20,
    height: 3,
    borderRadius: 1.5,
    opacity: 0.5,
  },
})

